import { isValidIdentity, type LevelIdentity } from '../model/identity';

/**
 * Persisted save schema.
 *
 * Every record read back from storage is validated before use: storage can be corrupted,
 * edited by hand, or written by a different application version. Nothing here is trusted.
 */

export const SAVE_SCHEMA_VERSION = 1;

export const STORE = {
  meta: 'meta',
  progress: 'progress',
  settings: 'settings',
  levels: 'levels',
  sessions: 'sessions',
} as const;

export type ThemePreference = 'system' | 'light' | 'dark';
export type MotionPreference = 'system' | 'full' | 'reduced';

export interface Settings {
  readonly theme: ThemePreference;
  readonly highContrast: boolean;
  readonly motion: MotionPreference;
  readonly soundEnabled: boolean;
  readonly soundVolume: number;
  readonly hapticsEnabled: boolean;
  readonly showBlockerHint: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  highContrast: false,
  motion: 'system',
  soundEnabled: true,
  soundVolume: 0.7,
  hapticsEnabled: true,
  showBlockerHint: true,
};

export interface LevelCompletion {
  readonly levelNumber: number;
  readonly boardHash: string;
  readonly bestTimeMs: number;
  readonly minMistakes: number;
  readonly minHintsUsed: number;
  readonly attempts: number;
  readonly generatorVersion: number;
  readonly stencilLibraryVersion: number;
  readonly contentVersion: number;
}

export interface CampaignProgress {
  readonly campaignId: string;
  readonly highestUnlockedLevel: number;
  readonly completions: Readonly<Record<number, LevelCompletion>>;
  readonly tutorialCompleted: boolean;
}

/** Compact resume record: the board itself is regenerated deterministically. */
export interface InterruptedLevelState {
  readonly identity: LevelIdentity;
  readonly boardHash: string;
  readonly removedArrowIds: readonly number[];
  readonly heartsRemaining: number;
  readonly elapsedMs: number;
  readonly hintsUsed: number;
  readonly mistakes: number;
}

export interface SaveMeta {
  readonly schemaVersion: number;
}

export function createEmptyProgress(campaignId: string): CampaignProgress {
  return { campaignId, highestUnlockedLevel: 1, completions: {}, tutorialCompleted: false };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFiniteInt(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}

function isRatio(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}

export function sanitizeSettings(value: unknown): Settings {
  if (!isRecord(value)) return DEFAULT_SETTINGS;
  const theme = value.theme;
  const motion = value.motion;
  return {
    theme:
      theme === 'light' || theme === 'dark' || theme === 'system' ? theme : DEFAULT_SETTINGS.theme,
    highContrast:
      typeof value.highContrast === 'boolean' ? value.highContrast : DEFAULT_SETTINGS.highContrast,
    motion:
      motion === 'full' || motion === 'reduced' || motion === 'system'
        ? motion
        : DEFAULT_SETTINGS.motion,
    soundEnabled:
      typeof value.soundEnabled === 'boolean' ? value.soundEnabled : DEFAULT_SETTINGS.soundEnabled,
    soundVolume: isRatio(value.soundVolume) ? value.soundVolume : DEFAULT_SETTINGS.soundVolume,
    hapticsEnabled:
      typeof value.hapticsEnabled === 'boolean'
        ? value.hapticsEnabled
        : DEFAULT_SETTINGS.hapticsEnabled,
    showBlockerHint:
      typeof value.showBlockerHint === 'boolean'
        ? value.showBlockerHint
        : DEFAULT_SETTINGS.showBlockerHint,
  };
}

const MAX_LEVEL_NUMBER = 100000;

export function sanitizeCompletion(value: unknown): LevelCompletion | null {
  if (!isRecord(value)) return null;
  if (!isFiniteInt(value.levelNumber, 1, MAX_LEVEL_NUMBER)) return null;
  if (typeof value.boardHash !== 'string' || !/^[0-9a-f]{16}$/.test(value.boardHash)) return null;
  return {
    levelNumber: value.levelNumber,
    boardHash: value.boardHash,
    bestTimeMs: isFiniteInt(value.bestTimeMs, 0, 1000 * 60 * 60 * 24) ? value.bestTimeMs : 0,
    minMistakes: isFiniteInt(value.minMistakes, 0, 3) ? value.minMistakes : 0,
    minHintsUsed: isFiniteInt(value.minHintsUsed, 0, 10000) ? value.minHintsUsed : 0,
    attempts: isFiniteInt(value.attempts, 0, 1000000) ? value.attempts : 1,
    generatorVersion: isFiniteInt(value.generatorVersion, 0, 1000) ? value.generatorVersion : 0,
    stencilLibraryVersion: isFiniteInt(value.stencilLibraryVersion, 0, 1000)
      ? value.stencilLibraryVersion
      : 0,
    contentVersion: isFiniteInt(value.contentVersion, 0, 1000) ? value.contentVersion : 0,
  };
}

export function sanitizeProgress(value: unknown, campaignId: string): CampaignProgress {
  if (!isRecord(value)) return createEmptyProgress(campaignId);

  const completions: Record<number, LevelCompletion> = {};
  const rawCompletions = value.completions;
  if (isRecord(rawCompletions)) {
    for (const key of Object.keys(rawCompletions)) {
      const levelNumber = Number.parseInt(key, 10);
      if (!isFiniteInt(levelNumber, 1, MAX_LEVEL_NUMBER)) continue;
      const completion = sanitizeCompletion(rawCompletions[key]);
      // Drop records whose key and payload disagree rather than silently trusting one.
      if (completion !== null && completion.levelNumber === levelNumber) {
        completions[levelNumber] = completion;
      }
    }
  }

  const highest = isFiniteInt(value.highestUnlockedLevel, 1, MAX_LEVEL_NUMBER)
    ? value.highestUnlockedLevel
    : 1;

  return {
    campaignId: typeof value.campaignId === 'string' ? value.campaignId : campaignId,
    highestUnlockedLevel: highest,
    completions,
    tutorialCompleted: value.tutorialCompleted === true,
  };
}

export function sanitizeInterruptedState(value: unknown): InterruptedLevelState | null {
  if (!isRecord(value)) return null;
  if (!isValidIdentity(value.identity)) return null;
  if (typeof value.boardHash !== 'string' || !/^[0-9a-f]{16}$/.test(value.boardHash)) return null;
  if (!Array.isArray(value.removedArrowIds)) return null;
  if (value.removedArrowIds.length > 100000) return null;
  for (const id of value.removedArrowIds) {
    if (!isFiniteInt(id, 0, 100000)) return null;
  }
  if (!isFiniteInt(value.heartsRemaining, 0, 3)) return null;

  return {
    identity: value.identity,
    boardHash: value.boardHash,
    removedArrowIds: value.removedArrowIds as number[],
    heartsRemaining: value.heartsRemaining,
    elapsedMs: isFiniteInt(value.elapsedMs, 0, 1000 * 60 * 60 * 24) ? value.elapsedMs : 0,
    hintsUsed: isFiniteInt(value.hintsUsed, 0, 10000) ? value.hintsUsed : 0,
    mistakes: isFiniteInt(value.mistakes, 0, 3) ? value.mistakes : 0,
  };
}
